// Developer Login Page - Separate from regular user login
import { APP_VERSION } from '../lib/version';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authApi } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

export function DeveloperLoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    loginname: '',
    passwort: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.loginname || !formData.passwort) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authApi.login(formData);
      const { user } = response.data;
      
      // Check if user is developer
      if (user.rolle !== 'DEVELOPER') {
        toast.error('Kein Developer-Zugang. Bitte normalen Login verwenden.');
        return;
      }
      
      setUser(user);
      toast.success('Developer-Login erfolgreich');
      navigate('/developer');
    } catch (error) {
      console.error('Developer login error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Anmeldung fehlgeschlagen';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <Shield className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white">
            Developer-Zugang
          </h1>
          <p className="text-slate-400 text-sm">
            System-Administration & Konfiguration
          </p>
        </div>

        {/* Login Card */}
        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl text-white">Anmelden</CardTitle>
            <CardDescription className="text-slate-400">
              Master-Zugangsdaten erforderlich
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-200">Benutzername</Label>
                <Input
                  type="text"
                  placeholder="developer"
                  value={formData.loginname}
                  onChange={(e) => setFormData(p => ({ ...p, loginname: e.target.value }))}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200">Master-Passwort</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.passwort}
                    onChange={(e) => setFormData(p => ({ ...p, passwort: e.target.value }))}
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 text-slate-400 hover:text-white"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Anmelden...
                  </>
                ) : (
                  'Developer-Login'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500">
          Nur für autorisierte Systemadministratoren
        </p>
        
        {/* Footer with Version */}
        <p className="text-center text-xs text-slate-600 mt-4">
          © {new Date().getFullYear()} Creativey Media · Version {APP_VERSION}
        </p>
      </div>
    </div>
  );
}

export default DeveloperLoginPage;
